import React, {useCallback, useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { TABLES } from '../../../../../Apis/Tables';
import TableFunction from '../../../../../components/table/TableFunction';
import { VIEW_TYPE } from '../../../../../enums/ViewType';
import { SimpleSearch } from '../../../../../reduxToolkit/Search/SearchApi';
import getTimezoneOffset from '../../../../../utils/GetTimeZone';
import GetDateFilter from '../../../Company/Payroll/Functionality/GetDateFilter';
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import AttendanceDayContainer from "../SubComponents/AttendanceDayContainer";
import {ATTENDANCE_MODES, MODEL_COLOR} from "../Models/Model";
import CustomImageTable from "../../../../../components/CustomImageTable";
import {IconAvatarTable} from "../../../../../components/buttons/IconButtons";
import CustomChip from "../../../../../components/CustomChip";
import {userType} from "../../../../../enums/userTypeEnum";
import {colors} from "../../../../../enums/colors";
import {EMPTY_VALUES} from "../../../../../Apis/functions";
import { SEARCH_MODEL } from '../../../../../components/table/Model/TableModel';
import CustomTooltip from "../../../../../components/CustomTooltip";

/**
 /**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @jobTittle: Full Stack Developer
 *
 */
const DiningAttendanceTableApp = ({
    setBodyForExport,
    finalArray,
    searchElement,
    setSearchElement,
    selectedOption,
    setSelectedOption,
    setRender,
    color,
    modified,
    setModified,
    prevIsDaily
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const tableId = "tbl_dat_attendance";
    const searchId = "tbl_dat_search_attendance";
    const { getAttendanceDate } = GetDateFilter();
    const headRef = useRef();
    const [attendanceStartDate, setAttendanceStartDate] = useState(getAttendanceDate(selectedOption).start);
    const [attendanceEndDate, setAttendanceEndDate] = useState( getAttendanceDate(selectedOption).end);
    const option = `${TABLES.FOODIE}`;

    // the axios interceptor drops the global overlay as soon as the response lands, but the day
    // columns are laid out several frames later: this covers that gap with our own loader
    const [loadingDays, setLoadingDays] = useState(false);

    const hideDaysLoader = useCallback(() => setLoadingDays(false), []);

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} classes="first_head w-190px">{t("name")}</CustomTh>,
            <CustomTh id="td_dat_second_column" tableId={tableId} headRef={headRef}/>,
        ]
    }

    const getTbody = () => {
        if (!results?.content?.length)
            return { body: [], data:[] };

        const body = results?.content.map((member) => {
            const userTypeName = t(userType[member?.userTypeId]) || EMPTY_VALUES.DEFAULT;

            return [
                <CustomTd key="name" tableId={tableId} fixed={true} left={0} tooltip={true}  classes="h-90px first-column w-190px border-shades-200">
                    <CustomImageTable image={IconAvatarTable} containerClass={"d-flex align-items-center"}>
                        <div className={"ml-1"}>
                            <div className={'flex-grow-1 minw-0 px-0'}>
                                <CustomTooltip
                                    text={`${member?.fullName || member?.name}`}
                                    classes="fs-14px ibl-font-w-600 font-color-general-950 text-ellipsis-1-row flex-grow-1 pb-1"
                                    showIfTruncated={true}
                                    placement="top"
                                />
                            </div>
                            <CustomChip legend={userTypeName}
                                        background={colors.secondary100}
                                        fontColor={colors.secondary700}
                                        borderColor={colors.secondary150}
                                        variant={'large'}
                            />
                        </div>
                    </CustomImageTable>
                </CustomTd>,
                <CustomTd key="days" tableId={tableId} classes={"h-90px"}/>,

            ]
        })

        return {
            body,
            data:results?.content
        }
    }

    /**
     * Keeps the floating day grid in sync with the current page.
     *
     * It lives outside getTbody on purpose: that function is invoked while the
     * table is building its rows, so setting state from inside its map coupled
     * the grid to the iteration and rebuilt it on every pass.
     */
    useEffect(() => {
        if (!results?.content?.length) {
            setRender('');
            setLoadingDays(false);
            return;
        }

        setRender(
            <AttendanceDayContainer
                color={color}
                currentDays={results.content[0]?.days}
                results={results.content}
                tableId={tableId}
                headRef={headRef}
                model={MODEL_COLOR}
                onRendered={hideDaysLoader}
            />
        );
    }, [results, color, tableId, setRender, hideDaysLoader]);

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = page => {
        if(prevIsDaily.current && searchElement !== "") return;
        setRender('');
        setResults({ content: [], totalElements: 0 });
        const {start, end} = getAttendanceDate(selectedOption);
        setAttendanceStartDate(start);
        setAttendanceEndDate(end);
        const timezone = getTimezoneOffset();

        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            attendanceTimeZone: timezone,
            attendanceStartDate: start,
            attendanceEndDate: end,
            attendanceSearchType: selectedOption,
            key: searchElement,
            model: option
        });
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (!success) return;
            // takes over right as the interceptor hides the global overlay
            setLoadingDays(true);
            setResults(data);
        });
    }

    const {thead, tbody, pagination, page, rowsPerPage } = TableFunction({
        data: results,
        getThead,
        getTbody,
        view: VIEW_TYPE.LIST.name,
        labelRowsPerPage: 'attendance_per_page',
        fixedPagination: false,
        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch:getData,//New - apply only with advanced search
        finalArray//New - apply only with advanced search
    });

    useEffect(() => {
        if(finalArray.length === 0 && !prevIsDaily.current){
            getData(0);
        }
        else if(finalArray.length > 0){
            setRender('');
            setSearchElement('');
            setSelectedOption(ATTENDANCE_MODES.daily);
        }
    }, [selectedOption]);

    useEffect(() => {
        if (finalArray.length > 0) {
            setRender('');
            setSearchElement('');
            setSelectedOption(ATTENDANCE_MODES.daily);
        }
    }, [finalArray]);

    return {
        option,
        page,
        rowsPerPage,
        attendanceStartDate,
        attendanceEndDate,
        selectedOption,
        results,
        setResults,
        selectedDate,
        setSelectedDate,
        pagination,
        tableId,
        searchId,
        thead,
        tbody,
        loadingDays
    };
};

export default DiningAttendanceTableApp;
